import axios from 'axios';
import { toast } from 'react-hot-toast';

const resolveBaseUrl = () => {
  const configured = process.env.REACT_APP_API_URL;

  if (configured && configured.trim()) {
    return configured.trim();
  }

  // A silent fallback here means production builds quietly point at localhost,
  // so surface it loudly instead.
  const message =
    'REACT_APP_API_URL is not set. Create .env.local (or set it in your host) before building.';

  if (process.env.NODE_ENV === 'production') {
    throw new Error(message);
  }

  console.warn(`${message} Falling back to http://localhost:4000/`);
  return 'http://localhost:4000/';
};

const BASE_URL = resolveBaseUrl();

const SESSION_TOAST_ID = 'session-expired';

// Prevents stacked "session expired" toasts when many requests 401/403 at once.
// Component .catch handlers also toast the API message — suppress those while redirecting.
let isSessionExpiring = false;

const originalToastError = toast.error.bind(toast);
toast.error = (content, options) => {
  if (isSessionExpiring && options?.id !== SESSION_TOAST_ID) {
    return SESSION_TOAST_ID;
  }
  return originalToastError(content, options);
};

export const isSessionExpiredError = (error) =>
  Boolean(error?.isSessionExpired || isSessionExpiring);

const handleSessionExpiry = (error, message, delayMs = 2000) => {
  if (error) {
    error.isSessionExpired = true;
  }

  if (isSessionExpiring) {
    return;
  }

  isSessionExpiring = true;
  toast.dismiss();
  originalToastError(message, { id: SESSION_TOAST_ID });
  setTimeout(() => {
    localStorage.clear();
    window.location.href = '/login';
  }, delayMs);
};

const fetchClient = () => {
  const instance = axios.create({
    baseURL: BASE_URL,
    timeout: 30000,
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
    },
  });

  instance.interceptors.response.use(
    (successRes) => {
      // Don't wipe the session-expired toast if a stray success sneaks through.
      if (!isSessionExpiring) {
        toast.dismiss();
      }
      return successRes;
    },
    (error) => {
      if (axios.isCancel(error) || error?.code === 'ERR_CANCELED') {
        return Promise.reject(error);
      }

      const status = error?.response?.status;
      const requestUrl = error?.config?.url || '';
      const isAdminDetailRequest = requestUrl.includes('/api/v1/admin_detail');
      const isLoginRequest = requestUrl.includes('/sessions');

      if (status === 401) {
        // Wrong current password for profile update can return 401.
        // Login failures should not force a redirect loop back to /login.
        if (!isAdminDetailRequest && !isLoginRequest) {
          handleSessionExpiry(error, error?.response?.data?.message || 'Unauthorized');
        } else if (isLoginRequest) {
          toast.error(error?.response?.data?.message || 'Please check your credentials');
        }
      }

      if (status === 403 && !isLoginRequest) {
        const message = error?.response?.data?.message || error?.response?.data?.error || 'Forbidden';
        if (
          message.toLowerCase().includes('restricted') ||
          message.toLowerCase().includes('pending approval') ||
          message.toLowerCase().includes('not active')
        ) {
          handleSessionExpiry(error, message, 1500);
        }
      }

      return Promise.reject(error);
    }
  );

  instance.interceptors.request.use((config) => {
    const isLoggedIn = !!localStorage.getItem('user');
    const token = localStorage.getItem('token');
    const url = config.url || '';

    if (isLoggedIn && !url.includes('s3.amazonaws.com')) {
      config.headers.Authorization = token ? `Bearer ${token}` : `Bearer ''`;
      config.headers.adminId = localStorage.getItem('adminId');
    }

    if (typeof FormData !== 'undefined' && config.data instanceof FormData) {
      delete config.headers['Content-Type'];
    }

    return config;
  });

  return instance;
};

export default fetchClient();
